import FounderBadge from "@/app/(custom-header)/botb-refer-a-friend/components/founderBadge";
import ArrowRightIcon from "@/app/shared-components/icons/ArrowRightIcon";
import { useBotbRAFContext } from "@/app/(custom-header)/botb-refer-a-friend/context";
import CheckmarkIcon from "@/app/shared-components/icons/CheckmarkIcon";
import GiftItem from "@/app/(custom-header)/botb-refer-a-friend/components/GiftItem";
import Info from "@/app/(custom-header)/botb-refer-a-friend/components/Info";
import { useIsMobile } from "@/app/redux/features/device/selectors";
import VerticalSeparator from "@/app/(custom-header)/botb-refer-a-friend/components/VerticalSeparator";

function GiftSection() {
  const { statistics, rafStatus, loading, setShowMoreDetails, preSubscriptionData, preSubscriptionDataLoading } =
    useBotbRAFContext();

  const isMobile = useIsMobile();

  const userLevel = {
    founder: "Founder",
    pioneer: "Pioneer",
    exclusive: "Exclusive",
  };

  const rewards: any = {
    howItWorks: "Treat your friends and treat yourself",
    exclusiveText: (
      <>
        Amazing work! <br className={"lg:hidden"} />
        You've made it all the way!
      </>
    ),
  };

  return (
    <div className="w-full flex flex-col gap-3.5 lg:gap-8 items-center font-jost">
      <div className={"flex flex-col items-center gap-2 lg:gap-2.5"}>
        <FounderBadge />
        <div
          className={`text-lg leading-6 font-semibold text-dark-1 text-center lg:text-3.5xl ${loading ? "opacity-0" : ""}`}>
          {loading
            ? "Treat your friends and treat yourself"
            : rafStatus.status === userLevel.exclusive
              ? rewards.exclusiveText
              : rewards.howItWorks}
        </div>
        {!loading && rafStatus.referredFriends === rafStatus.maximumInvites && (
          <div className={"flex items-center gap-1.5 font-jost"}>
            <CheckmarkIcon variation={3} className={"size-6 shrink-0"} />
            <span className={"text-[#28282B] text-lg font-semibold leading-6"}>
              {rafStatus.referredFriends}/{rafStatus.maximumInvites}
            </span>
            <span className={"text-[#898994] text-base font-medium leading-[22px]"}>Friends Invited</span>
          </div>
        )}
      </div>

      <div className={"flex flex-col items-center gap-3.5 lg:gap-4 w-full"}>
        {isMobile ? (
          <div
            className={`w-full relative bg-gray-4 lg:px-10 lg:py-4 py-4 rounded-lg flex flex-col items-center justify-center gap-6 lg:min-w-[694px] ${rafStatus.referredFriends === rafStatus.maximumInvites ? "px-5" : "px-3"}`}>
            <div
              className={`w-full flex justify-center gap-3 lg:gap-8 ${rafStatus.referredFriends === rafStatus.maximumInvites ? "items-start" : "items-center"}`}>
              {!loading && rafStatus.referredFriends === 0 && (
                <>
                  {/*#1*/}
                  <GiftItem
                    label={"Treat your friends"}
                    offerDesc={
                      <>
                        {!preSubscriptionDataLoading && preSubscriptionData?.offer?.description
                          ? preSubscriptionData?.offer?.description.split(" ").slice(1).join(" ")
                          : ""}
                      </>
                    }
                    imgSrc={"/assets/images/refer-a-friend/dream-house.webp"}
                    offerValue={`${!preSubscriptionDataLoading && preSubscriptionData?.offer?.description ? `${preSubscriptionData?.offer?.description.split(" ")[0]}x` : "10x"}`}
                    classes={"flex-1 lg:max-w-[300px]"}
                  />

                  <VerticalSeparator classes={"self-end"} />

                  {/*#2*/}
                  <GiftItem
                    label={"Treat yourself"}
                    offerDesc={<>Cash</>}
                    imgSrc={"/assets/images/refer-a-friend/cash-earned.webp"}
                    offerValue={`£${rafStatus.cashPerInvite}`}
                    textClasses={"!text-primary !text-sm !font-extrabold lg:!text-[22px] !leading-4 lg:!leading-[26px]"}
                    classes={"flex-1 lg:max-w-[300px]"}
                  />
                </>
              )}

              {!loading && rafStatus.referredFriends > 0 && (
                <div className={"w-full flex flex-col gap-4"}>
                  <div className={"flex gap-3"}>
                    {/*#1*/}
                    <GiftItem
                      label={"Potential Earnings"}
                      offerDesc={
                        <>
                          Cash <br />
                          Earned
                        </>
                      }
                      imgSrc={"/assets/images/refer-a-friend/cash-earned.webp"}
                      offerValue={`£${rafStatus.cashPerInvite * rafStatus.referredFriends}`}
                      classes={"flex-1"}
                    />

                    <VerticalSeparator
                      classes={`${rafStatus.referredFriends === rafStatus.maximumInvites ? "mx-2 self-center" : "self-end"}`}
                    />

                    {/*#2*/}
                    <GiftItem
                      label={"Gifts Sent"}
                      offerDesc={<>Gift{rafStatus.referredFriends > 1 && "s"} Sent</>}
                      imgSrc={"/assets/images/refer-a-friend/dream-house.webp"}
                      offerValue={rafStatus.referredFriends}
                      classes={"flex-1"}
                    />
                  </div>

                  {!loading && rafStatus.referredFriends !== rafStatus.maximumInvites && (
                    <div className={"h-px bg-black/8"} />
                  )}

                  <div className={"flex gap-3"}>
                    {/*#3*/}
                    <GiftItem
                      offerDesc={
                        <>
                          Friend{rafStatus.referredFriends > 1 && "s"}
                          <br /> invited
                        </>
                      }
                      imgSrc={"/assets/images/refer-a-friend/user-plus.webp"}
                      offerValue={rafStatus.referredFriends}
                      textClasses={"min-w-[57px]"}
                      classes={rafStatus.referredFriends === rafStatus.maximumInvites ? "flex-1" : ""}
                    />

                    {!loading &&
                      rafStatus.referredFriends > 0 &&
                      rafStatus.referredFriends < rafStatus.maximumInvites && (
                        <Info
                          classes={"ml-auto"}
                          content={
                            <>
                              You get{" "}
                              <strong className={"font-medium text-[#28282B]"}>
                                £{rafStatus.cashPerInvite}/friend
                              </strong>{" "}
                              and they
                              <br /> get{" "}
                              {!preSubscriptionDataLoading && preSubscriptionData?.offer?.description
                                ? preSubscriptionData?.offer?.description
                                : ""}
                            </>
                          }
                        />
                      )}

                    {!loading && rafStatus.referredFriends === rafStatus.maximumInvites && (
                      <VerticalSeparator classes={"self-center mx-2"} />
                    )}

                    {/*#4*/}
                    {!loading && rafStatus.referredFriends === rafStatus.maximumInvites && (
                      <div className={"flex flex-col gap-2 flex-1"}>
                        <GiftItem
                          label={"Missions"}
                          offerDesc={
                            <>
                              Missions <br />
                              Completed
                            </>
                          }
                          imgSrc={"/assets/images/refer-a-friend/milestones-icon.webp"}
                          offerValue={`${statistics.filter(item => item.tasks?.[0]?.is_completed === true).length}/${statistics.length}`}
                        />
                        <div>
                          <button
                            onClick={() => setShowMoreDetails({ show: true, tab: 1 })}
                            className={"theme-btn theme-btn-blue theme-btn-3xs rounded-lg min-w-[104px]"}>
                            See missions
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div
            className={
              "relative bg-gray-4 px-3 lg:px-10 lg:py-6 py-4 rounded-lg flex flex-col items-center justify-center gap-6 lg:min-w-[694px]"
            }>
            <div
              className={`flex justify-center ${rafStatus.referredFriends === rafStatus.maximumInvites ? "items-start gap-8" : "items-center gap-11"}`}>
              {!loading && rafStatus.referredFriends === 0 && (
                <>
                  {/*#1*/}
                  <GiftItem
                    label={"Treat your friends"}
                    offerDesc={
                      <>
                        {!preSubscriptionDataLoading && preSubscriptionData?.offer?.description
                          ? preSubscriptionData?.offer?.description.split(" ").slice(1).join(" ")
                          : ""}
                      </>
                    }
                    imgSrc={"/assets/images/refer-a-friend/dream-house.webp"}
                    offerValue={`${!preSubscriptionDataLoading && preSubscriptionData?.offer?.description ? `${preSubscriptionData?.offer?.description.split(" ")[0]}x` : "10x"}`}
                    classes={"flex-1 lg:max-w-[300px]"}
                  />

                  <VerticalSeparator />

                  {/*#2*/}
                  <GiftItem
                    label={"Treat yourself"}
                    offerDesc={<>Cash</>}
                    imgSrc={"/assets/images/refer-a-friend/cash-earned.webp"}
                    offerValue={`£${rafStatus.cashPerInvite}`}
                    textClasses={"!text-primary !text-sm !font-extrabold lg:!text-[22px] !leading-4 lg:!leading-[26px]"}
                    classes={"flex-1 lg:max-w-[300px]"}
                  />
                </>
              )}

              {!loading && rafStatus.referredFriends > 0 && (
                <>
                  {/*#1*/}
                  <GiftItem
                    label={"Potential Earnings"}
                    offerDesc={
                      <>
                        Cash <br />
                        Earned
                      </>
                    }
                    imgSrc={"/assets/images/refer-a-friend/cash-earned.webp"}
                    offerValue={`£${rafStatus.cashPerInvite * rafStatus.referredFriends}`}
                  />

                  <VerticalSeparator classes={rafStatus.referredFriends === rafStatus.maximumInvites ? "my-5" : ""} />

                  {/*#2*/}
                  <GiftItem
                    label={"Gifts Sent"}
                    offerDesc={<>Gift{rafStatus.referredFriends > 1 && "s"} Sent</>}
                    imgSrc={"/assets/images/refer-a-friend/dream-house.webp"}
                    offerValue={rafStatus.referredFriends}
                    classes={"lg:max-w-[230px]"}
                  />

                  <VerticalSeparator classes={rafStatus.referredFriends === rafStatus.maximumInvites ? "my-5" : ""} />

                  {/*#3*/}
                  <GiftItem
                    label={"Your Referrals"}
                    offerDesc={
                      <>
                        Friends <br />
                        invited
                      </>
                    }
                    imgSrc={"/assets/images/refer-a-friend/user-plus.webp"}
                    offerValue={rafStatus.referredFriends}
                  />
                </>
              )}

              {/*#4*/}
              {!loading && rafStatus.referredFriends === rafStatus.maximumInvites && (
                <>
                  <VerticalSeparator classes={rafStatus.referredFriends === rafStatus.maximumInvites ? "my-5" : ""} />

                  <div className={"flex flex-col gap-6"}>
                    <GiftItem
                      label={"Missions"}
                      offerDesc={
                        <>
                          Missions <br />
                          Completed
                        </>
                      }
                      imgSrc={"/assets/images/refer-a-friend/milestones-icon.webp"}
                      offerValue={`${statistics.filter(item => item.tasks?.[0]?.is_completed === true).length}/${statistics.length}`}
                    />
                    <div>
                      <button
                        onClick={() => setShowMoreDetails({ show: true, tab: 1 })}
                        className={"theme-btn theme-btn-blue theme-btn-md rounded-lg w-full"}>
                        See missions
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {!loading && rafStatus.referredFriends > 0 && rafStatus.referredFriends < rafStatus.maximumInvites && (
              <Info
                content={
                  <>
                    You get <strong className={"font-medium text-[#28282B]"}>£{rafStatus.cashPerInvite}/friend</strong>{" "}
                    and they get{" "}
                    {!preSubscriptionDataLoading && preSubscriptionData?.offer?.description
                      ? preSubscriptionData?.offer?.description
                      : ""}
                  </>
                }
              />
            )}
          </div>
        )}

        <div className={"w-full flex justify-start lg:justify-center"}>
          <button
            className={
              "text-[#696971] font-jost text-xs font-medium leading-4 lg:text-base lg:leading-[22px] flex gap-1 items-center underline cursor-pointer"
            }
            onClick={() => setShowMoreDetails({ show: true, tab: 0 })}>
            How it works
            <ArrowRightIcon className={"size-4 shrink-0"} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default GiftSection;
